import { useEffect, useState, useMemo } from "react";
import { Header, Loader } from "../../components";
import { useAuthContext } from "../../context/auth";
import { getEdificiosListadoPublico } from "../../utils";
import styles from "./stylesListadoB2Bnuevo.module.scss";

// Mismo criterio "en instalación" que el Cotizador: INSTALACIÓN + REACTIVACIÓN.
const ESTADOS_INSTALACION = ["INSTALACIÓN", "REACTIVACIÓN"];
const esInstalacion = (estado) => ESTADOS_INSTALACION.includes(estado);

// Opciones del segmented (estado en JS, sobre lo ya traído por país en la query).
const ESTADO_FILTROS = [
  { key: "OPERATIVOS", label: "Operativos" },
  { key: "INSTALACION", label: "Instalación" },
  { key: "TODOS", label: "Todos" },
];

const calculateEmpresasCantidad = (empresaslistado) => {
  if (!empresaslistado) {
    return 0;
  }
  if (empresaslistado.toUpperCase() === "NO DISPONIBLE") {
    return 0;
  }
  const empresasArray = empresaslistado.split(",");
  return empresasArray.length;
};

const tieneEmpresas = (empresaslistado) => calculateEmpresasCantidad(empresaslistado) > 0;

function ListadoB2Bnuevo() {
  const [edificios, setEdificios] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEdificio, setSelectedEdificio] = useState(""); // Filtro por edificio
  const [estadoFilter, setEstadoFilter] = useState("TODOS"); // Segmented: Operativos / En instalación / Todos
  const { pais } = useAuthContext();

  useEffect(() => {
    const initialize = async () => {
      const newEdificios = await getEdificiosListadoPublico({ pais });
      setEdificios(newEdificios);
    };
    initialize();
  }, [pais]);

  const edificiosOptions = useMemo(() => {
    if (!edificios) return [];
    const uniqueEdificios = [...new Set(edificios.map(({ edificio }) => edificio))].sort();
    return ["Todos", ...uniqueEdificios];
  }, [edificios]);

  const filteredEdificios = useMemo(() => {
    if (!edificios) return [];
    const term = searchTerm.trim().toUpperCase();
    return [...edificios]
      .filter(({ empresaslistado, edificio, estado }) => {
        // Capa 1 — estado (segmented), en JS sobre lo ya traído por país en la query
        const matchesEstado =
          estadoFilter === "TODOS" ||
          (estadoFilter === "OPERATIVOS" && !esInstalacion(estado)) ||
          (estadoFilter === "INSTALACION" && esInstalacion(estado));
        if (!matchesEstado) return false;

        // Capa 2 — dropdown de edificio
        const matchesEdificio =
          selectedEdificio === "" || selectedEdificio === "Todos" || edificio === selectedEdificio;
        if (!matchesEdificio) return false;

        // Capa 3 — búsqueda de empresa
        // Fila placeholder "PRÓXIMAMENTE" (en instalación sin empresas): no tiene
        // empresas que matchear, así que solo es visible sin término de búsqueda.
        // Rama propia y explícita, no colgada del ternario de empresaslistado.
        if (esInstalacion(estado) && !tieneEmpresas(empresaslistado)) {
          return term === "";
        }

        // Filas con empresas (operativas o en instalación con inquilinos).
        // Los operativos sin empresas siguen ocultos, igual que hoy.
        return empresaslistado ? empresaslistado.toUpperCase().includes(term) : false;
      })
      .sort((a, b) => a.edificio.localeCompare(b.edificio));
  }, [edificios, searchTerm, selectedEdificio, estadoFilter]);

  const totalEmpresasCantidad = useMemo(() => {
    if (edificios && edificios.length > 0) {
      return edificios.reduce((total, edificio) => total + calculateEmpresasCantidad(edificio.empresaslistado), 0);
    }
    return 0;
  }, [edificios]);

  const highlightText = (text, highlight) => {
    if (!highlight) return text;
    const regex = new RegExp(`(${highlight})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className={styles.highlight}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className={styles.listadoB2BContainer}>
      <Header
        leftContent={
          <div className={styles.statsContainer}>
            <span className={styles.statLine}>
              Cantidad de empresas inquilinas en los edificios:{" "}
              <strong>{totalEmpresasCantidad.toLocaleString("es-CL")}</strong>
            </span>
          </div>
        }
      >
        <div className={styles.filterRow}>
          <div className={styles.segmented} role="group" aria-label="Filtrar por estado">
            {ESTADO_FILTROS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`${styles.segmentedBtn} ${estadoFilter === opt.key ? styles.segmentedBtnActive : ""}`}
                onClick={() => setEstadoFilter(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Buscar empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.filterInput}
          />
          <select
            value={selectedEdificio}
            onChange={(e) => setSelectedEdificio(e.target.value)}
            className={styles.filterSelect}
          >
            {edificiosOptions.map((edificio) => (
              <option key={edificio} value={edificio}>
                {edificio}
              </option>
            ))}
          </select>
        </div>
      </Header>
      {edificios ? (
        <div className={styles.tableContainer}>
          <table className={`${styles.table} table table-striped table-hover table-bordered table-sm`}>
            <thead className="table-dark align-middle">
              <tr>
                <th scope="col" className="col-2 text-start">
                  EDIFICIO
                </th>
                <th scope="col" className="col-1 text-center">
                  CANTIDAD
                </th>
                <th scope="col" className="col-9 text-center">
                  EMPRESAS
                </th>
              </tr>
            </thead>
            <tbody className="table-striped align-middle">
              {filteredEdificios.map(({ id, edificio, empresaslistado, estado }) => {
                const enInstalacion = esInstalacion(estado);
                const esPlaceholder = enInstalacion && !tieneEmpresas(empresaslistado);
                return (
                  <tr key={id}>
                    <td className="col-2 text-start">
                      <div className={styles.edificioCell}>
                        <span>{edificio.toUpperCase()}</span>
                        {enInstalacion && <span className={styles.badgeInstalacion}>Instalación</span>}
                      </div>
                    </td>
                    <td className="col-1 text-center">{calculateEmpresasCantidad(empresaslistado)}</td>
                    <td className="col-9 text-center">
                      {esPlaceholder ? (
                        <span className={styles.proximamente}>PRÓXIMAMENTE</span>
                      ) : empresaslistado ? (
                        highlightText(empresaslistado.toUpperCase(), searchTerm)
                      ) : (
                        ""
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  );
}

export default ListadoB2Bnuevo;
